import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useGetIdentity, useGetMany } from "ra-core";
import { FileText, MessageSquare, PenLine, X } from "lucide-react";
import {
  InlineIconButton,
  Pressable,
  TextButton,
} from "@/components/ui/pressable";
import { cn } from "@/lib/utils";
import { DRAFTS_CHANGED_EVENT } from "@/lib/formPersistence/formDraftStorage";
import type { ClientInvoice, Ticket } from "@/modules/types";
import { clearTicketReplyDraft } from "@/modules/tickets/ticketReplyDraftStorage";
import { ticketShowPath } from "@/modules/tickets/ticketStatusWorkflow";
import { formatInvoiceMoney } from "@/modules/billing/invoiceEmailTemplate";
import { clearFormDraft } from "@/lib/formPersistence/formDraftStorage";
import { getMessagesConversationPath } from "@/modules/messages/messagesRouting";
import {
  listInvoiceDrafts,
  listSmsDrafts,
  listTicketReplyDrafts,
  smsDraftStorageKey,
  untrackInvoiceDraft,
  type TrayDraft,
} from "@/modules/drafts/draftsRegistry";

const MAX_VISIBLE = 3;

/** Ticket or invoice open on the current page, so its own chip is hidden. */
const openRecord = (pathname: string, search: string) => {
  const params = new URLSearchParams(search);
  return {
    ticketId:
      pathname.match(/^\/tickets\/(\d+)/)?.[1] ?? params.get("ticket") ?? null,
    invoiceId:
      pathname.match(/^\/billing\/invoices\/(\d+)/)?.[1] ??
      params.get("invoice") ??
      null,
    creatingInvoice: pathname.startsWith("/billing/invoices/new"),
    conversationId: pathname.startsWith("/messages")
      ? params.get("conversation")
      : null,
  };
};

/**
 * Gmail-style tray of work left half-done (ticket replies, invoices saved
 * as Draft): click to go back exactly where you were; × to discard.
 */
export const DraftsTray = () => {
  const { identity } = useGetIdentity();
  const memberId = identity?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    window.addEventListener(DRAFTS_CHANGED_EVENT, bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener(DRAFTS_CHANGED_EVENT, bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const drafts = useMemo(
    () =>
      memberId == null
        ? []
        : [
            ...listTicketReplyDrafts(memberId),
            ...listInvoiceDrafts(),
            ...listSmsDrafts(memberId),
          ].sort((left, right) => right.savedAt - left.savedAt),
    // `version` and the route re-read the browser storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberId, version, location.key],
  );

  const ticketIds = drafts.flatMap((draft) =>
    draft.kind === "ticket-reply" ? [draft.ticketId] : [],
  );
  const invoiceIds = drafts.flatMap((draft) =>
    draft.kind === "invoice" ? [draft.invoiceId] : [],
  );
  const conversationIds = drafts.flatMap((draft) =>
    draft.kind === "sms" ? [draft.conversationId] : [],
  );
  const { data: conversations = [] } = useGetMany<{
    id: string | number;
    title?: string | null;
    external_phone?: string | null;
  }>(
    "conversations",
    { ids: conversationIds },
    { enabled: conversationIds.length > 0 },
  );
  const { data: tickets = [] } = useGetMany<Ticket>(
    "tickets",
    { ids: ticketIds },
    { enabled: ticketIds.length > 0 },
  );
  const { data: invoices = [], isSuccess: invoicesLoaded } =
    useGetMany<ClientInvoice>(
      "client_invoices",
      { ids: invoiceIds },
      { enabled: invoiceIds.length > 0 },
    );

  // Invoices that were sent or deleted leave the tray.
  useEffect(() => {
    if (!invoicesLoaded) return;
    for (const id of invoiceIds) {
      const row = invoices.find((invoice) => String(invoice.id) === id);
      if (!row || row.status !== "draft") untrackInvoiceDraft(id);
    }
  }, [invoiceIds, invoices, invoicesLoaded]);

  const current = openRecord(location.pathname, location.search);
  const visible = drafts.filter((draft) =>
    draft.kind === "ticket-reply"
      ? draft.ticketId !== current.ticketId
      : draft.kind === "sms"
        ? draft.conversationId !== current.conversationId
        : !current.creatingInvoice && draft.invoiceId !== current.invoiceId,
  );
  if (!visible.length) return null;

  const describe = (draft: TrayDraft) => {
    if (draft.kind === "ticket-reply") {
      const ticket = tickets.find((row) => String(row.id) === draft.ticketId);
      return {
        key: `t-${draft.ticketId}`,
        icon: PenLine,
        title: `Reply · #${draft.ticketId}`,
        detail: ticket?.subject?.trim() || "Ticket",
        href: ticketShowPath(draft.ticketId),
        discardLabel: "Discard this reply draft?",
        discard: () =>
          memberId != null && clearTicketReplyDraft(memberId, draft.ticketId),
      };
    }
    if (draft.kind === "sms") {
      const conversation = conversations.find(
        (row) => String(row.id) === draft.conversationId,
      );
      return {
        key: `s-${draft.conversationId}`,
        icon: MessageSquare,
        title: "SMS",
        detail:
          conversation?.title?.trim() ||
          conversation?.external_phone ||
          "Conversation",
        href: getMessagesConversationPath(draft.conversationId),
        discardLabel: "Discard this SMS draft?",
        discard: () =>
          memberId != null &&
          clearFormDraft(smsDraftStorageKey(memberId, draft.conversationId)),
      };
    }
    const invoice = invoices.find((row) => String(row.id) === draft.invoiceId);
    return {
      key: `i-${draft.invoiceId}`,
      icon: FileText,
      title: invoice?.invoice_number ?? "Invoice draft",
      detail: invoice
        ? formatInvoiceMoney(Number(invoice.amount) || 0, invoice.currency)
        : "Draft",
      href: `/billing?invoice=${draft.invoiceId}`,
      discardLabel: "Remove from tray? The invoice stays as Draft.",
      discard: () => untrackInvoiceDraft(draft.invoiceId),
    };
  };

  const shown = expanded ? visible : visible.slice(0, MAX_VISIBLE);
  const hidden = visible.length - shown.length;

  return (
    <div
      // Bottom center: the bottom-right corner holds Send buttons.
      className="pointer-events-none fixed bottom-3 left-1/2 z-40 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap-reverse justify-center gap-2 print:hidden"
      aria-label="Unsent drafts"
    >
      {shown.map((draft) => {
        const item = describe(draft);
        const Icon = item.icon;
        const confirming = confirmKey === item.key;
        return (
          <div
            key={item.key}
            className="pointer-events-auto flex max-w-72 items-center gap-1 rounded-full border bg-background py-1 pr-1 pl-3 text-sm shadow-overlay"
          >
            {confirming ? (
              <>
                <span className="truncate text-xs">{item.discardLabel}</span>
                <TextButton
                  tone="destructive"
                  onClick={() => {
                    item.discard();
                    setConfirmKey(null);
                  }}
                >
                  Yes
                </TextButton>
                <TextButton
                  className="pr-2"
                  onClick={() => setConfirmKey(null)}
                >
                  No
                </TextButton>
              </>
            ) : (
              <>
                <Pressable
                  className="flex min-w-0 items-center gap-2 text-left"
                  title="Continue where you left off"
                  onClick={() => navigate(item.href)}
                >
                  <Icon className="size-3.5 shrink-0 text-primary" />
                  <span className="truncate font-medium">{item.title}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {item.detail}
                  </span>
                </Pressable>
                <InlineIconButton
                  size="sm"
                  aria-label="Discard draft"
                  title="Discard"
                  onClick={() => setConfirmKey(item.key)}
                >
                  <X />
                </InlineIconButton>
              </>
            )}
          </div>
        );
      })}
      {hidden > 0 ? (
        <Pressable
          className={cn(
            "pointer-events-auto rounded-full border bg-background px-3 py-1 text-sm shadow-overlay",
          )}
          onClick={() => setExpanded(true)}
        >
          +{hidden} more
        </Pressable>
      ) : null}
    </div>
  );
};
